<script setup>
import {TransitionRoot, TransitionChild, Dialog, DialogPanel, DialogTitle} from '@headlessui/vue'

const props = defineProps({
  confirmationMessage: { type: String, default: '' },
  confirmButtonText: { type: String, default: 'Yes' },
  confirmLabel: { type: String, default: '' },
  singleOkButton: {type: Boolean, default: false},
  warningIcon: {type: Boolean, default: false},
  subTitle: {type: String, default: ''},
  successIcon: {type: Boolean, default: false},
  mainConfirmButtonClasses: {type: String, default: ''},
  singleOkButtonText: {type: String, default: 'OK'},
  logo: {type: String, default: null}
})
const emit = defineEmits(['confirmResult'])

const confirmAction = () => {
  emit('confirmResult', true)
}

const cancelAction = () => {
  emit('confirmResult', false)
}
</script>

<template>
    <TransitionRoot as="template" :show="true">
    <Dialog as="div" class="relative z-[9999]" @close="cancelAction">
      <TransitionChild
        as="template"
        enter="ease-out duration-300"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="ease-in duration-200"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <div class="fixed inset-0 bg-black bg-opacity-[0.38] transition-opacity backdrop-blur-sm" />
      </TransitionChild>
  
      <div class="fixed inset-0 z-10 overflow-y-auto">
        <div class="flex min-h-full w-full justify-center md:items-center md:p-[30px]">
          <TransitionChild
            as="template"
            enter="ease-out duration-300"
            enter-from="opacity-0 translate-y-4 md:translate-y-0 md:scale-95"
            enter-to="opacity-100 translate-y-0 md:scale-100"
            leave="ease-in duration-200"
            leave-from="opacity-100 translate-y-0 md:scale-100"
            leave-to="opacity-0 translate-y-4 md:translate-y-0 md:scale-95"
          >
            <DialogPanel class="relative rounded-lg bg-white p-6 shadow-xl transition-all w-full max-w-[26rem] flex-1 text-center">
              <section>
                <header v-if="props.confirmLabel" class="flex flex-col items-center gap-2">
                  <div v-if="props.logo" class="border border-gray-200 rounded-md p-1">
                    <img :src="props.logo" class="w-6 h-6" />
                  </div>
                  <h2 class="text-lg font-semibold leading-6 text-gray-900 pb-2">
                    {{ props.confirmLabel }}
                  </h2>
                </header>

                <img v-if="props.successIcon" src="@/assets/3d-assets/icons/check.png" class="mx-auto w-[88px] my-6" />

                <div class="flex justify-center relative">
                  <Icon v-if="props.warningIcon" icon="heroicons:exclamation-triangle" class="text-2xl text-yellow-400 absolute top-0 left-0" />

                  <DialogTitle class="mb-5 whitespace-pre-line flex flex-col gap-y-2">  
                    <span v-if="props.subTitle" class="text-base font-medium text-gray-900">
                      {{ props.subTitle }}
                    </span>
                   
                    <slot>
                      <template v-if="props.confirmationMessage">
                        {{ props.confirmationMessage }}
                      </template>
                    </slot>
                    
                  </DialogTitle>
                </div>
                <div v-if="!singleOkButton" class="flex items-stretch gap-x-3">
                  <button class="text-xs flex-1" :class="props.mainConfirmButtonClasses || 'primary-button'" @click="confirmAction" @keydown.enter="confirmAction">
                    {{ props.confirmButtonText }}
                  </button>
                  <button class="alternative-button text-xs flex-1" @click="cancelAction" @keydown.esc="cancelAction">
                    Cancel
                  </button>
                </div>
                <div v-else class="flex items-centered justify-center gap-x-3">
                  <button class="alternative-button text-xs flex-1" @click="cancelAction" @keydown.enter="cancelAction">
                    {{ props.singleOkButtonText || 'OK' }}
                  </button>
                </div>
              </section>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>

</template>